<template>
  <div id="user-editor-modal">

    <ErrorPopup
      :error="error"
    />

    <memlist-modal ref="edit-user" size="xl" :ok-title="$t('COMMON.SAVE')" hide-footer>
    
      <div class="ml-tabs">
        <div class="ml-tabs-header">
          <button
            type="button"
            v-for="(tab, i) in tabs"
            :key="i"
            :class="['ml-tab-button', active_tab === i ? 'active' : '']"
            @click="active_tab = i"
            :disabled="tab.disabled"
          >
            {{ $t(tab.label) }}
          </button>
        </div>

        <div class="ml-tabs-body">
          <div v-show="active_tab === 0">
      
            <UserUpdatePasswordModal
              ref="update-password-modal"
              :user="user"
            />

            <form class="mt-8" @submit.prevent="submit_form">

              
              <m-row>
                <m-col md="6" sm="12">
                  <memlist-text-input
                    id="email"
                    name="email"
                    layout="vertical"
                    :title="$t('USER.EMAIL')"
                    :placeholder="$t('USER.EMAIL')"
                    validation_type="TEXT"
                    v-model="user.email"
                    :validate="force_validate"
                    :invalid_text="$t('COMMON.INVALID_EMAIL')"
                  />
                </m-col>
              </m-row>

              <m-row>
                <m-col lg="6" sm="12">
                  <memlist-select-input2
                    id="role"
                    name="role"
                    layout="vertical"
                    :title="$t('USER.ROLE')"
                    validation_type="TEXT"
                    v-model="user.role"
                    :items="role_options"
                    :validate="force_validate"
                    :invalid_text="$t('COMMON.INVALID_ROLE')"
                  />
                </m-col>
                <m-col lg="6" sm="12">
                  <div class="d-flex align-items-end">
                    <div class="flex-grow-1">
                      <memlist-select-input2
                        id="group_id"
                        name="group_id"
                        :title="$t('USER.GROUP')"
                        validation_type="TEXT"
                        v-model="user.group_id"
                        :items="group_options"
                        :validate="force_validate"
                        :invalid_text="$t('COMMON.INVALID_GROUP')"
                      />
                    </div>
                    <memlist-table-button
                      v-if="user.group_id"
                      class="ml-3 mb-2"
                      @click="remove_from_group(user.group_id, user.user_id)"
                      tooltip="Ta bort från grupp"
                      svg="/assets/svg/trash2.svg"
                    />
                  </div>
                </m-col>
                
              </m-row>

              <m-row>
                <m-col md="6" sm="12">
                  <memlist-text-input
                    id="firstname"
                    name="firstname"
                    layout="vertical"
                    :title="$t('USER.FIRSTNAME')"
                    validation_type="TEXT"
                    v-model="user.firstname"
                  />
                </m-col>
                <m-col md="6" sm="12">
                  <memlist-text-input
                    id="lastname"
                    name="lastname"
                    layout="vertical"
                    :title="$t('USER.LASTNAME')"
                    validation_type="TEXT"
                    v-model="user.lastname"
                  />
                </m-col>
              </m-row>

              <m-row>
                <m-col md="6" sm="12">
                  <memlist-text-input
                    id="title"
                    name="title"
                    layout="vertical"
                    :title="$t('USER.TITLE')"
                    validation_type="TEXT"
                    v-model="user.title"
                  />
                </m-col>
                <m-col md="6" sm="12">
                  <memlist-select-input2
                    id="audience_id"
                    name="audience_id"
                    layout="vertical"
                    :title="$t('USER.AUDIENCE')"
                    validation_type="TEXT"
                    v-model="user.audience_id"
                    :items="audiences_options"
                  />
                  <router-link to="/ml-advanced-search" tag="a" class="line-link blue-link">
                    {{$t('ADVANCED_SEARCH.EDIT_AUDIENCE')}}
                  </router-link>
                </m-col>
              </m-row>

              <m-row>
                <m-col md="6" sm="12">
                  <memlist-text-input
                    id="in_personnr"
                    name="in_personnr"
                    layout="vertical"
                    :title="$t('USER.PERSONNR')"
                    validation_type="TEXT"
                    v-model="user.in_personnr"
                  />
                </m-col>
                <m-col md="6" sm="12">
                  <memlist-text-input
                    id="phone"
                    name="phone"
                    layout="vertical"
                    :title="$t('USER.PHONE')"
                    validation_type="TEXT"
                    v-model="user.phone"
                  />
                </m-col>
              </m-row>

              <m-row>
                <m-col md="6" sm="12">
                  <memlist-text-input
                    id="slack_id"
                    name="slack_id"
                    layout="vertical"
                    :title="$t('USER.SLACK_ID')"
                    validation_type="TEXT"
                    v-model="user.slack_id"
                  />
                </m-col>
              </m-row>


              <RightModalSaveAndCloseButtons
                class="mt-4"
                :text="$t('COMMON.SAVE')"
                :spin="true"
                @clicked="submit_form"
                @close="on_cancel"
                ref="saveButton"
              />

            </form>
          
          </div>

          <div v-show="active_tab === 1">

            <UserEditorTerms
              :user="user"
              :user_id="local_user_id"
            />

          </div>

          <div v-show="active_tab === 2">
            <TokenEditor
              class="mb-4"
              :user="user"
            />
          </div>
          <div v-show="active_tab === 3">
            <THSUserEditor
              :user="user"
            />
          </div>
        </div>
      </div>
    </memlist-modal>

  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { toasts } from '@/core/mixins/toastr-helper.mixin.js';

import UserEditorTerms from '@/view/pages/ml/users/components/UserEditorTerms.vue';
import TokenEditor from '@/view/pages/ml/users/components/TokenEditor.vue';
import THSUserEditor from '@/view/pages/ml/users/components/THSUserEditor.vue';
import UserUpdatePasswordModal from '@/view/pages/ml/users/components/UserUpdatePasswordModal.vue';
import ErrorPopup from '@/view/components/ErrorPopup.vue';
import { useVuelidate } from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';
import { validate_state } from '@/core/services/validations.service';

import RightModalSaveAndCloseButtons from '@/view/components/buttons/RightModalSaveAndCloseButtons.vue';

export default {
  name: 'UserEditorModal',
  components: {
    
    UserEditorTerms,
    TokenEditor,
    THSUserEditor,
    UserUpdatePasswordModal,
    ErrorPopup,
    RightModalSaveAndCloseButtons
  },
  mixins: [ toasts ],
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      user: {
        email: {
          required,
          email,
        },
        role: {
          required,
        },
        group_id: {
          required,
        }
      }
    };
  },
  computed: {
    tabs() {
      let base = [
        { label: 'USER.DETAILS', disabled: false },
        { label: 'USER.TERMS', disabled: this.user.user_id === null },
        { label: 'ACCESS.TOKEN.API_ACCESS', disabled: this.user.user_id === null },
        
      ];

      if (this.isTHS) {
        base.push({ label: 'COMMON.THS', disabled: this.user.user_id === null });
      }
      
      return base;
    },
    ...mapGetters(['currentCompanyId', 'currentPeriodId', 'pages', 'isTHS']),
    role_options() {
      return [
        { value: 'ADMIN', text: this.$t('USER.ROLES.ADMIN') },
        { value: 'OWNER', text: this.$t('USER.ROLES.OWNER') },
        { value: 'USER', text: this.$t('USER.ROLES.USER') },
        { value: 'NONE', text: this.$t('USER.ROLES.NONE') },
      ]
    },
    group_options() {
      const options = [];

      if (!this.groups) {
        return [];
      }

      for (const group of this.groups) {
        options.push({
          value: group.id,
          text: group.name
        });
      }

      return options;
    },
    email_state() {
      return validate_state({ dirty: this.v$.user.email.$dirty, error: this.v$.user.email.$error });
    },
    role_state() {
      return validate_state({ dirty: this.v$.user.role.$dirty, error: this.v$.user.role.$error });
    },
    group_id_state() {
      return validate_state({ dirty: this.v$.user.group_id.$dirty, error: this.v$.user.group_id.$error });
    },
  },
  props: ['user_id','groups'],
  emits: ['created', 'updated'],
  async mounted() {
    await this.get_audiences();
  },
  methods: {
    on_cancel() {
      this.$refs['edit-user'].hide();
    },
    open_update_password() {
      this.$refs['update-password-modal'].show();
    },

    async remove_from_group(group_id, user_id) {
      try {
        const result = await axios.delete(`/access/group/${group_id}/user/${user_id}`);

        if (result.status === 204) {
          this.toastr('success', this.$t('COMMON.OK'), this.$t('ACCESS.GROUP.USER_REMOVED'));
          this.user.group_id = null;
          return;
        }
      }
      catch (err) {
        console.error(err);
      }

      this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('ACCESS.GROUP.UNABLE_REMOVE_USER'));
    },

    async submit_form() {
      if (this.user.email) {
        this.user.email = this.user.email.trim();
      }

      const is_valid = await this.v$.$validate();

      if (!is_valid) {
        this.v$.$touch();
        this.$refs['saveButton'].stop();
        return;
      }

      try {
        if (this.user.user_id) {
          await this.put_user(this.user);
        }
        else {
          await this.post_user(this.user);
        }

        this.$refs['saveButton'].stop();
        this.$refs['edit-user'].hide();
      }
      catch (err) {
        console.error('submit_form error', err);
        this.$refs['saveButton'].stop();
      }

    },

    show() {
      this.$refs['edit-user'].show();

      this.$nextTick(()=>{
        if (this.local_user_id) {
          this.get_user(this.local_user_id);
        }
      });
    },

    edit_user_clicked(item) {
      this.user = item;
      this.$refs['edit-user'].show();
    },

    new_user_clicked() {
      this.user = {};
      this.$refs['edit-user'].show();
    },

    async get_audiences() {
      try {
        const res = await axios.get(`/user/audiences`);

        if (res.status === 200) {

          this.audiences_options = res.data.map(item => ({ value: item.id, text: item.name }));

          return;
        }

        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('USER.UNABLE_GET'));
      }
      catch (err) {
        console.error(err);
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('USER.UNABLE_GET'));
      }
    },

    async get_user(id) {
      try {
        const res = await axios.get(`/access/user/${id}`);

        if (res.status === 200) {
          this.user = res.data;

          if (this.user.users_audiences) {
            this.audience_id = this.user.users_audiences.length > 0 ? this.user.users_audiences[0].audience_id : null;
          }
          else {
            this.audience_id = null;
          }
          
          return;
        }

        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('USER.UNABLE_GET'));
      }
      catch (err) {
        console.error(err);
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('USER.UNABLE_GET'));
      }
    },

    async put_user(user) {
      try {
        const res = await axios.put(`/access/user/${user.user_id}`, user);

        if (res.status === 200) {
          this.toastr('success', this.$t('COMMON.SUCCESS'), this.$t('USER.UPDATED'));

          this.$emit('updated', res.data);

          return;
        }

        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('USER.UNABLE_UPDATE'));
      }
      catch (err) {
        console.error(err);
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('USER.UNABLE_UPDATE'));
      }
    },

    async post_user(user) {
      try {
        const res = await axios.post(`/access/user`, user);

        if (res.status === 201) {
          this.toastr('success', this.$t('COMMON.SUCCESS'), this.$t('USER.CREATED'));

          if (this.audience_id) {
            await axios.post(`/user/${res.data.user_id}/audience/${this.audience_id}`);
          }

          this.$emit('created', res.data);

          return res.data;
        }

        else if (res.status === 409) {
          if (res.data.reason.indexOf('invite template') >= 0) {

            this.error = 'ERR_INVITE_TEMPLATE_NOT_SET';

            this.$nextTick(()=>{ this.error = null; });
          }
          else if (res.data.reason.indexOf('taken') >= 0) {
            this.error = 'ERR_EMAIL_ALREADY_REGISTERED';

            this.$nextTick(()=>{ this.error = null; });

          }
        }

        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('USER.UNABLE_CREATE'));
      }
      catch (err) {
        console.error(err);
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('USER.UNABLE_CREATE'));
      }
    },

    reset() {
      this.local_user_id = null;
      this.user = {};
    },

  },
  data() {
    return {
      
      active_tab: 0,
      audiences_options: [],
      local_user_id: null,
      error: null,
      user: {},
      assigned_group_id: 1,
    };
  },
  watch: {
    
    user_id(new_val, old_val) {
      this.local_user_id = new_val;
    },
    local_user_id(new_val, old_val) {
      if (new_val !== null) {
        this.get_user(new_val);
      }
    },
  },

};
</script>
<style lang="scss" scoped>

@import "@/assets/sass/components/forms/_memlist_tabs2.scss";

</style>